import { useNavigate } from '@tanstack/react-router';
import { ChevronsUpDown, LogOut, UserCircle, UserRound } from 'lucide-react';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useLogout } from '@/features/auth/hooks/useLogout';
import { cn } from '@/lib/utils';
import { useAuthStore } from '@/store/auth.store';
import { FLOATING_MENU_ITEM_CLASS, FLOATING_PANEL_CLASS } from '@/styles/floatingPanel';
import { getInitialsFromName } from '@/utils/getInitialsFromName';

const TRIGGER_CLASS = cn(
  'flex items-center gap-1.5 rounded-full bg-card/80 py-1 pr-2 pl-1 outline-none md:gap-2 md:pr-3',
  'ring-1 ring-primary/15 shadow-[0_6px_18px_-12px_color-mix(in_oklab,var(--color-primary)_60%,transparent)]',
  'transition-[box-shadow,color] hover:ring-primary/35 focus-visible:ring-2 focus-visible:ring-ring/50 data-popup-open:ring-primary/45',
);
const MENU_CLASS = cn('w-64 p-1.5', FLOATING_PANEL_CLASS);
const ITEM_ICON_TILE_CLASS = 'flex size-7 items-center justify-center rounded-md';
const AVATAR_FALLBACK_CLASS =
  'bg-linear-to-br from-primary to-primary-strong text-2xs font-semibold text-primary-foreground';

interface UserAvatarProps {
  initials: string;
  className: string;
}

function UserAvatar({ initials, className }: UserAvatarProps) {
  return (
    <Avatar className={className}>
      <AvatarFallback className={AVATAR_FALLBACK_CLASS}>
        {initials === '' ? <UserRound className="size-3.5" /> : initials}
      </AvatarFallback>
    </Avatar>
  );
}

export function UserMenu() {
  const user = useAuthStore((s) => s.user);
  const navigate = useNavigate();
  const { mutate: logout } = useLogout();
  const userInitials = getInitialsFromName(user?.fullName);

  return (
    <DropdownMenu modal={true}>
      <DropdownMenuTrigger className={TRIGGER_CLASS}>
        <UserAvatar initials={userInitials} className="size-7" />
        <span className="hidden max-w-40 truncate text-sm font-medium text-secondary-foreground md:block">
          {user?.fullName}
        </span>
        <ChevronsUpDown className="size-3.5 shrink-0 text-muted-foreground md:size-4" />
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" sideOffset={8} className={MENU_CLASS}>
        <div className="flex items-center gap-3 rounded-xl bg-muted/60 px-2.5 py-2.5">
          <UserAvatar initials={userInitials} className="size-9" />
          <div className="flex min-w-0 flex-col">
            <span className="text-sm font-semibold wrap-break-word">{user?.fullName}</span>
            <span className="text-xs wrap-break-word text-muted-foreground">{user?.username}</span>
          </div>
        </div>
        <DropdownMenuSeparator className="my-1.5" />
        <DropdownMenuItem
          onClick={() => navigate({ to: '/profile' })}
          className={FLOATING_MENU_ITEM_CLASS}
        >
          <span className={cn(ITEM_ICON_TILE_CLASS, 'bg-muted text-muted-foreground')}>
            <UserCircle className="size-4" />
          </span>
          Profile
        </DropdownMenuItem>
        <DropdownMenuItem
          variant="destructive"
          onClick={() => logout()}
          className={FLOATING_MENU_ITEM_CLASS}
        >
          <span className={cn(ITEM_ICON_TILE_CLASS, 'bg-destructive/10')}>
            <LogOut className="size-4" />
          </span>
          Logout
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
